import React from 'react';
import { FiHome, FiUsers, FiTrendingUp,  FiCheckCircle, FiShield, FiBarChart2, FiGlobe, FiZap } from 'react-icons/fi';

const AllInstitutionERP = () => {
  const challenges = [
    {
      icon: <FiHome className="w-6 h-6" />,
      title: 'Each Institution Has Its Own Personality',
      stat: '83% of educational groups struggle with ERP integration',
      description: 'Every school, college, or coaching center under a trust has its own way of doing things. One school uses a particular grading system. Another follows a different academic calendar. One has 500 students, the other has 2000. Yet most ERP vendors expect you to fit all your institutions into a single, rigid template that doesn\'t respect their individuality.',
      solution: 'Truyon understands that no two institutions are alike. Each school gets its own instance with custom settings, while the trust gets a unified view across all institutions.',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'The Separate Systems Nightmare',
      stat: 'Educational groups use 5+ different software systems',
      description: 'One ERP for the main school. A different system for the college. A third for the coaching center. Spreadsheets for some functions. Separate accounting software. The list goes on. Nothing talks to each other. Administrators spend their days switching between systems, exporting data from one, importing to another, and praying everything matches.',
      solution: 'Truyon is built for multi-institution management from the ground up. One platform, all your institutions. Data flows seamlessly, and you see everything from a single dashboard.',
      color: 'bg-amber-50'
    },
    {
      icon: <FiTrendingUp className="w-6 h-6" />,
      title: 'The Growth Problem',
      stat: '70% of trusts struggle to add new institutions to their ERP',
      description: 'Your trust is growing. You acquired a new school. You started a new college. You opened a coaching center in a new city. But your ERP can\'t handle it. Every new institution means a new system, new training, new data migration, new headaches. Growth becomes stressful instead of exciting.',
      solution: 'Truyon scales effortlessly. Add a new institution with a few clicks. All the features your existing institutions have, ready for your new one. Growth becomes simple.',
      color: 'bg-rose-50'
    },
    {
      icon: <FiBarChart2 className="w-6 h-6" />,
      title: 'No Central Visibility',
      stat: 'Trusts waste 40% of their time on manual reporting',
      description: 'How are all your institutions performing? Which one needs attention? Where are the best practices emerging? Without a unified view, you\'re flying blind. Reports from different systems don\'t match. You end up creating manual spreadsheets to get any kind of overview—or worse, making decisions with incomplete information.',
      solution: 'Truyon gives you a unified dashboard. See performance across all institutions at a glance. Compare, analyze, and act with confidence.',
      color: 'bg-purple-50'
    },
    {
      icon: <FiShield className="w-6 h-6" />,
      title: 'Data Security Across Locations',
      stat: 'Schools face 3x higher compliance risks without unified systems',
      description: 'Different institutions, different teachers, different data, different locations. Yet some centralized oversight is needed. But without a unified platform, security becomes a nightmare. Different systems have different security levels. Data is scattered. Access control is inconsistent. It\'s a data protection disaster waiting to happen.',
      solution: 'Truyon provides enterprise-grade security with granular access control. Define who sees what, across all your institutions. Student data stays secure, no matter where your institutions are located.',
      color: 'bg-blue-50'
    },
    {
      icon: <FiGlobe className="w-6 h-6" />,
      title: 'Inconsistent Processes',
      stat: '80% of trusts report inconsistent processes across institutions',
      description: 'The main campus has a smooth admission process. The new campus is a mess. The school has an efficient fee collection system. The college is struggling. Standards vary widely. Your brand reputation suffers because some institutions are perceived as well-run while others are chaotic. Consistency is the secret to building trust in your brand.',
      solution: 'Truyon ensures consistent processes across all institutions. Share best practices, standardize procedures, and maintain quality standards everywhere.',
      color: 'bg-emerald-50'
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        Educational trusts, groups, and chains are the backbone of Indian education. A single trust might run multiple schools, colleges, coaching centers, or a combination of all three. It's a beautiful vision—scaling quality education across communities.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        But here's the problem. <strong className="text-slate-900">Most ERPs were built for a single institution.</strong> They assume one principal, one admin team, one set of processes, one location. For educational groups with multiple institutions, this becomes a nightmare.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        You're forced to either use multiple systems that don't talk to each other, or force all your institutions into a rigid template that doesn't respect their unique needs. Neither works. Let's talk about what actually happens when your ERP can't handle all your institutions.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">83%</div>
          <div className="text-sm text-slate-500">Struggle with ERP integration</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">5+</div>
          <div className="text-sm text-slate-500">Different software systems used</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-rose-600">70%</div>
          <div className="text-sm text-slate-500">Struggle to add new institutions</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">Why Your ERP Can't Handle Multiple Institutions</h2>

      {/* Challenges List */}
      {challenges.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <h3 className="text-xl font-bold text-slate-900">{item.title}</h3>
                <span className="bg-white/80 px-3 py-1 rounded-full text-xs font-bold text-amber-600 border border-amber-200">
                  {item.stat}
                </span>
              </div>
              <p className="text-slate-600 leading-relaxed mb-4 text-sm">{item.description}</p>
              <div className="bg-white rounded-xl p-4 border border-emerald-200">
                <div className="flex items-center gap-2 text-sm font-semibold text-emerald-600 mb-1">
                  <FiCheckCircle className="w-4 h-4" />
                  <span>Truyon's Solution</span>
                </div>
                <p className="text-sm text-slate-700">{item.solution}</p>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Comparison Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">The Real Difference: Multiple Systems vs. Truyon</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-sm font-semibold text-red-700 mb-2">Multiple Systems</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Each institution has its own ERP</span></li>
              <li className="flex items-start gap-2">• <span>Data doesn't sync across systems</span></li>
              <li className="flex items-start gap-2">• <span>No central visibility of all institutions</span></li>
              <li className="flex items-start gap-2">• <span>Adding new institution means new implementation</span></li>
              <li className="flex items-start gap-2">• <span>Multiple training programs for staff</span></li>
              <li className="flex items-start gap-2">• <span>Inconsistent processes across institutions</span></li>
            </ul>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">With Truyon Unified Platform</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>One platform for all institutions</span></li>
              <li className="flex items-start gap-2">• <span>Real-time data sync across institutions</span></li>
              <li className="flex items-start gap-2">• <span>Complete visibility from a central dashboard</span></li>
              <li className="flex items-start gap-2">• <span>Add new institutions in minutes</span></li>
              <li className="flex items-start gap-2">• <span>Single training program for all staff</span></li>
              <li className="flex items-start gap-2">• <span>Consistent processes with local flexibility</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-3">What Educational Groups Gain with Truyon</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
            <FiGlobe className="w-6 h-6 text-indigo-600 mb-2" />
            <h4 className="font-bold text-slate-900 text-sm">Central Oversight</h4>
            <p className="text-xs text-slate-600">Monitor every institution from a single dashboard. Know what's happening everywhere, anytime.</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <FiTrendingUp className="w-6 h-6 text-emerald-600 mb-2" />
            <h4 className="font-bold text-slate-900 text-sm">Consistent Quality</h4>
            <p className="text-xs text-slate-600">Share best practices across institutions. Maintain standards everywhere.</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
            <FiZap className="w-6 h-6 text-amber-600 mb-2" />
            <h4 className="font-bold text-slate-900 text-sm">Effortless Growth</h4>
            <p className="text-xs text-slate-600">Add new institutions without new implementations. Growth becomes simple and smooth.</p>
          </div>
        </div>
      </div>

      {/* The Real Story Section */}
      <div className="mt-8 bg-amber-50 rounded-2xl p-6 border border-amber-200">
        <h3 className="text-lg font-bold text-amber-900 mb-3">The True Cost of Disconnected Institutions</h3>
        <p className="text-amber-800 leading-relaxed mb-3">
          When your ERP can't handle all your institutions, you pay the price in different ways. Your trust administrators spend hours reconciling reports from different systems. Your finance team struggles with inconsistent data. Your principals feel isolated, unable to learn from sister institutions. 
        </p>
        <p className="text-amber-800 leading-relaxed mb-3">
          But the biggest cost is missed opportunity. When you can't see across all your institutions, you miss the chance to identify what's working and replicate it. You can't spot problems before they become crises. You can't make strategic decisions because you don't have the full picture.
        </p>
        <p className="text-amber-800 leading-relaxed">
          Your vision of scaling quality education requires the right infrastructure. Truyon gives you that infrastructure.
        </p>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">One Platform. All Your Institutions. Complete Control.</h3>
        <p className="text-indigo-800 leading-relaxed">
          Educational trusts are building the future of India. They deserve technology that matches their ambition—not old, disconnected systems that hold them back.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon is built for educational groups. One platform that respects the individuality of each institution while giving you the unified view you need. Effortless growth. Consistent quality. Complete visibility.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Your institutions are diverse. Your ERP shouldn't be.</strong>
        </p>
      </div>
    </>
  );
};

export default AllInstitutionERP;